import React, { FC, useEffect, useState, useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Edit, Undo } from '@mui/icons-material';
import { Tooltip, TextField, InputAdornment, IconButton } from '@mui/material';
import { toast } from 'react-toastify';
import { useUpdateApplicationManagementMutation } from 'reduxState/store/applicationManagement/api';
import { ConfirmEdit } from './ConfirmEdit';
import { ApplicationManagement } from '../../../reduxState/store/applicationManagement/types';
import MultipleSelectDropdown, { MultipleSelectOption } from 'components/MultipleSelectDropdown/MultipleSelectDropdown';
import { countryCodeOptions, groupedCountryCodeOptions, GroupedCountries } from 'constants/countryCodes';
import { addGroupedCountryValues } from 'pages/CreateApplication/helpers/helpers';
import '../scss/AppName.scss';

export interface AppServiceableCountriesProps {
  applicationId: string;
  ServiceableCountries: string[];
  applicationManagement: ApplicationManagement | null;
}

export const AppServiceableCountries: FC<React.PropsWithChildren<AppServiceableCountriesProps>> = ({
  applicationId,
  ServiceableCountries,
  applicationManagement,
}) => {
  const { user } = useAuth0();
  const [isDisabled, setDisabled] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<MultipleSelectOption[]>([]);
  const [updateApplicationManagement] = useUpdateApplicationManagementMutation();
  
  const serviceableCountriesOptions = useMemo(
    () => [...groupedCountryCodeOptions, ...countryCodeOptions],
    []
  );

  // Map country codes array to dropdown options
  const mapServiceableCountriesToOptions = useCallback(
    (countries: string[]): MultipleSelectOption[] => {
      if (!countries || countries.length === 0) {
        return [];
      }

      return countries
        .map(countryCode => {
          // First check if it's a grouped country
          if (countryCode === GroupedCountries.ALL_COUNTRIES) {
            return groupedCountryCodeOptions.find(opt => opt.value === GroupedCountries.ALL_COUNTRIES);
          }
          // Then check individual countries
          return serviceableCountriesOptions.find(opt => opt.value === countryCode);
        })
        .filter((opt): opt is MultipleSelectOption => opt !== undefined);
    },
    [serviceableCountriesOptions]
  );

  // Map current ServiceableCountries array to dropdown options
  useEffect(() => {
    const mappedOptions = mapServiceableCountriesToOptions(ServiceableCountries || []);
    setSelectedCountries(mappedOptions);
  }, [ServiceableCountries, mapServiceableCountriesToOptions]);

  const saveServiceableCountries = async (): Promise<boolean> => {
    if (!applicationManagement) return false;
    
    // Extract country codes from selected options
    const countryCodes = selectedCountries.map(option => option.value);
    
    // Determine the serviceable countries array to send
    // If "All Countries" is selected, send empty array (server will handle expansion)
    // If no countries selected, send empty array
    // Otherwise, expand grouped countries to individual country codes
    let serviceableCountriesArray: string[] = [];
    if (countryCodes.length > 0 && !countryCodes.includes(GroupedCountries.ALL_COUNTRIES)) {
      // Expand grouped countries (e.g., "US + US Territories", "Europe") to individual country codes
      serviceableCountriesArray = addGroupedCountryValues(countryCodes);
    }
    // If countryCodes is empty or includes "All Countries", serviceableCountriesArray remains empty
    
    const author = user?.email || 'unknown-user';
    setIsSaving(true);
    
    try {
      await updateApplicationManagement({
        appId: applicationId,
        applicationManagement: {
          Name: applicationManagement.Name,
          ProductPageURL: applicationManagement.ProductPageURL,
          Options: applicationManagement.Options,
          Country: applicationManagement.Country,
          ServiceableCountries: serviceableCountriesArray,
        },
        author,
      }).unwrap();
      
      toast.success('Serviceable Countries updated successfully');
      return true;
    } catch (error) {
      toast.error('Failed to update Serviceable Countries. Please try again.');
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const toggleDisabled = (): void => setDisabled(prevState => !prevState);

  const handleChange = (valuesArray: MultipleSelectOption[]): void => {
    setSelectedCountries(valuesArray);
  };

  const cancelEdit = (): void => {
    // Reset to original values
    const mappedOptions = mapServiceableCountriesToOptions(ServiceableCountries || []);
    setSelectedCountries(mappedOptions);
    setDisabled(true);
  };

  const handleSave = async (): Promise<void> => {
    const success = await saveServiceableCountries();
    if (success) {
      toggleDisabled();
    }
  };

  const getDisplayText = (): string => {
    if (selectedCountries.length === 0) return 'No countries selected';
    return selectedCountries.map(opt => opt.label).join(', ');
  };

  // Get display value for read-only mode (show friendly labels instead of codes)
  const getReadOnlyDisplayValue = (): string => {
    if (!ServiceableCountries || ServiceableCountries.length === 0) return '';
    
    const displayLabels = ServiceableCountries.map(countryCode => {
      if (countryCode === GroupedCountries.ALL_COUNTRIES) {
        return GroupedCountries.ALL_COUNTRIES;
      }
      const option = serviceableCountriesOptions.find(opt => opt.value === countryCode);
      return option?.label || countryCode;
    });
    
    return displayLabels.join(', ');
  };

  // When disabled, show as read-only text field with edit button
  if (isDisabled) {
    const displayValue = getReadOnlyDisplayValue();
    return (
      <div className="application-name settings-form-group">
        <TextField
          id="serviceableCountries"
          className="settings-form-group"
          label="Serviceable Countries"
          value={displayValue}
          disabled
          variant="outlined"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Edit Serviceable Countries">
                  <IconButton
                    onClick={toggleDisabled}
                    aria-label="Edit Serviceable Countries"
                    data-testid="edit-button"
                    size="small"
                  >
                    <Edit className="edit-icon" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </div>
    );
  }

  // When editing, show the multi-select dropdown
  return (
    <div className="application-name settings-form-group">
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%' }}>
        <div style={{ flex: 1, width: '100%' }}>
          <MultipleSelectDropdown
            label="Serviceable Countries"
            options={serviceableCountriesOptions}
            selectedValues={selectedCountries}
            placeholder="Select Serviceable Countries"
            noOptionsText="No Countries Found"
            handleChange={handleChange}
          />
        </div>
        <div style={{ marginTop: '8px', flexShrink: 0 }}>
          <Tooltip title="Cancel editing">
            <IconButton
              onClick={cancelEdit}
              aria-label="Cancel editing"
              data-testid="undo-button"
              size="small"
              disabled={isSaving}
            >
              <Undo className="edit-icon-undo" />
            </IconButton>
          </Tooltip>
        </div>
      </div>
      <ConfirmEdit
        headerText={'Edit Serviceable Countries'}
        confirmText={`Confirm save to update serviceable countries:`}
        changeText={getDisplayText()}
        onSave={handleSave}
      />
      {isSaving && <div style={{ marginTop: '8px', color: '#666' }}>Saving...</div>}
    </div>
  );
};
